import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import { BIZ } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SERVICE_OPTS = ["Roofing", "Siding", "Gutters", "Not sure yet"];

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-600">{label}</span>
    {children}
  </label>
);

export const EstimateForm = ({ defaultService = "Roofing", defaultAddress = "" }) => {
  const [f, setF] = useState({ name: "", phone: "", email: "", address: defaultAddress, service: defaultService, message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/estimates`, { ...f, email: f.email || null, page: window.location.pathname });
      setDone(true);
      toast.success("Request received! We'll call you shortly.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(Array.isArray(d) ? `Please check: ${d.map((x) => x?.loc?.slice(-1)[0]).join(", ")}` : `Something went wrong. Please call ${BIZ.phone}.`);
    } finally {
      setBusy(false);
    }
  };

  if (done) return (
    <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="py-10 text-center" data-testid="estimate-success">
      <CheckCircle2 className="mx-auto h-14 w-14 text-amber-500" />
      <h3 className="font-display mt-4 text-3xl font-bold uppercase text-slate-900">Thank you, {f.name.split(" ")[0]}!</h3>
      <p className="mx-auto mt-3 max-w-sm text-slate-600">Your request is in. We'll reach out soon to schedule your free estimate. Need us sooner? Call <a href={BIZ.tel} className="font-bold text-amber-700">{BIZ.phone}</a>.</p>
    </motion.div>
  );

  return (
    <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2" data-testid="estimate-form">
      <Field label="Full name *"><input required minLength={2} autoComplete="name" value={f.name} onChange={set("name")} className="inp" placeholder="Jane Smith" data-testid="estimate-name-input" /></Field>
      <Field label="Phone *"><input required minLength={7} type="tel" autoComplete="tel" value={f.phone} onChange={set("phone")} className="inp" placeholder="(414) 555-0123" data-testid="estimate-phone-input" /></Field>
      <Field label="Email"><input type="email" autoComplete="email" value={f.email} onChange={set("email")} className="inp" placeholder="you@email.com" data-testid="estimate-email-input" /></Field>
      <Field label="Service needed">
        <select value={f.service} onChange={set("service")} className="inp" data-testid="estimate-service-select">
          {SERVICE_OPTS.map((s) => <option key={s}>{s}</option>)}
        </select>
      </Field>
      <div className="sm:col-span-2"><Field label="Project address"><input autoComplete="street-address" value={f.address} onChange={set("address")} className="inp" placeholder="Street, City, WI" data-testid="estimate-address-input" /></Field></div>
      <div className="sm:col-span-2"><Field label="Tell us about your project"><textarea rows={3} value={f.message} onChange={set("message")} className="inp resize-none" placeholder="Leak over the garage, want new siding color..." data-testid="estimate-message-input" /></Field></div>
      <button disabled={busy} className="btn-amber group justify-center py-4 text-base sm:col-span-2" data-testid="estimate-form-submit-button">
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Request my free estimate <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" /></>}
      </button>
      <p className="text-center text-xs text-slate-500 sm:col-span-2">Prefer to talk? Call or text <a href={BIZ.tel} className="font-semibold text-slate-800">{BIZ.phone}</a></p>
    </form>
  );
};
